import React, { useMemo, useState } from 'react';
import { X, Search, Info, Check } from 'lucide-react';
interface Product {
  id: string;
  name: string;
  sku: string;
  thumbnail: string;
}
interface SubstituteMapping {
  [productId: string]: string | null; // productId -> substituteProductId
}
interface ConfigureSubstitutesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (mappings: SubstituteMapping) => void;
}
// Mock product data
const MOCK_PRODUCTS: Product[] = [{
  id: '1',
  name: '2024 Shiraz',
  sku: 'SHZ-2024',
  thumbnail: '🍷'
}, {
  id: '2',
  name: '2024 Cabernet Sauvignon',
  sku: 'CAB-2024',
  thumbnail: '🍷'
}, {
  id: '3',
  name: '2024 Merlot',
  sku: 'MER-2024',
  thumbnail: '🍷'
}, {
  id: '4',
  name: '2024 Pinot Noir',
  sku: 'PIN-2024',
  thumbnail: '🍷'
}, {
  id: '5',
  name: '2024 Chardonnay',
  sku: 'CHR-2024',
  thumbnail: '🍷'
}, {
  id: '6',
  name: '2024 Sauvignon Blanc',
  sku: 'SAU-2024',
  thumbnail: '🍷'
}, {
  id: '7',
  name: '2023 Reserve Shiraz',
  sku: 'RSH-2023',
  thumbnail: '🍷'
}, {
  id: '8',
  name: '2023 Reserve Cabernet',
  sku: 'RCA-2023',
  thumbnail: '🍷'
}];
export function ConfigureSubstitutesModal({
  isOpen,
  onClose,
  onSave
}: ConfigureSubstitutesModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [substitutes, setSubstitutes] = useState<SubstituteMapping>({});
  const [autoLinked, setAutoLinked] = useState<Set<string>>(new Set());
  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return MOCK_PRODUCTS;
    const query = searchQuery.toLowerCase();
    return MOCK_PRODUCTS.filter(p => p.name.toLowerCase().includes(query) || p.sku.toLowerCase().includes(query));
  }, [searchQuery]);
  // Handle substitute selection with bidirectional linking
  const handleSubstituteChange = (productId: string, substituteId: string) => {
    setSubstitutes(prev => {
      const newSubstitutes = {
        ...prev
      };
      // Remove old bidirectional link if exists
      const oldSubstitute = prev[productId];
      if (oldSubstitute && newSubstitutes[oldSubstitute] === productId) {
        delete newSubstitutes[oldSubstitute];
        setAutoLinked(prevAuto => {
          const newAuto = new Set(prevAuto);
          newAuto.delete(oldSubstitute);
          return newAuto;
        });
      }
      if (substituteId === '') {
        // Clear substitute
        delete newSubstitutes[productId];
        setAutoLinked(prevAuto => {
          const newAuto = new Set(prevAuto);
          newAuto.delete(productId);
          return newAuto;
        });
      } else {
        // Set new substitute
        newSubstitutes[productId] = substituteId;
        // Auto-link the reverse (if not already set manually)
        if (!newSubstitutes[substituteId]) {
          newSubstitutes[substituteId] = productId;
          setAutoLinked(prevAuto => new Set(prevAuto).add(substituteId));
        }
      }
      return newSubstitutes;
    });
  };
  // Count configured products
  const configuredCount = Object.keys(substitutes).filter(key => substitutes[key] !== null).length;
  const handleSave = () => {
    onSave(substitutes);
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-1003" data-mp="1h903qqyrjlpq">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0F172A] bg-opacity-40" onClick={onClose} data-id="element-1004" data-mp="1cb8398k1yr9x" />

      {/* Modal - Constrained to viewport with flex layout */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[640px] max-h-[85vh] flex flex-col overflow-hidden" data-id="element-1005" data-mp="24xj32gzu4o5o">
        {/* Fixed Header - Does not scroll */}
        <div className="flex-shrink-0" data-id="element-1006" data-mp="1v899ewpcexfg">
          {/* Title Section */}
          <div className="p-6 border-b border-[#E2E8F0] flex items-start justify-between" data-id="element-1007" data-mp="1v7zjf94asq9r">
            <div data-id="element-1008" data-mp="2sp7yi67ydsqu">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-1009" data-mp="2spq59ws8rpd0">
                Configure Substitutes
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-1010" data-mp="28f6s8vwrjq46">
                Set up replacement products for out-of-stock items
              </p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-[#F1F5F9] rounded transition-colors" data-id="element-1011" data-mp="2dbx7jh0quhoj">
              <X className="w-5 h-5 text-[#64748B]" data-id="element-1012" data-mp="1yiu2ua893yf8" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="px-6 pt-4 pb-3" data-id="element-1013" data-mp="2r5eqazllvqdi">
            <div className="relative" data-id="element-1014" data-mp="2r6g77kpaz735">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-1015" data-mp="2r7r739fmnuoz" />
              <input type="text" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-[40px] pl-10 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow" data-id="element-1016" data-mp="2w6c8vdaj2mfj" />
            </div>
          </div>

          {/* Info Banner */}
          <div className="px-6 pb-4" data-id="element-1017" data-mp="13fgxfaun2clo">
            <div className="p-3 bg-[#E0F2FE] rounded-lg flex items-start gap-2" data-id="element-1018" data-mp="13efll45stm2w">
              <Info className="w-4 h-4 text-[#0284C7] flex-shrink-0 mt-0.5" data-id="element-1019" data-mp="yfw1l9armkd6" />
              <p className="text-[13px] text-[#0284C7]" data-id="element-1020" data-mp="1d6bjof7vxc74">
                Substitutes are linked both ways. If Product A substitutes for
                Product B, then B will also substitute for A.
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable Product List - Takes remaining space */}
        <div className="flex-1 flex-shrink overflow-y-auto px-6" data-id="element-1021" data-mp="fjcwqw6zvimf">
          {filteredProducts.length === 0 ? <div className="flex flex-col items-center justify-center py-12" data-id="element-1022" data-mp="tim2f1xkquc5">
              <div className="text-4xl mb-3" data-id="element-1023" data-mp="pfzg1gjoa1mc">📦</div>
              <p className="text-[14px] font-medium text-[#0F172A] mb-1" data-id="element-1024" data-mp="pibxlsliq9yv">
                No products match your search
              </p>
              <button onClick={() => setSearchQuery('')} className="text-[14px] text-[#0EA5E9] hover:underline" data-id="element-1025" data-mp="20vxkf73wnzh8">
                Clear search
              </button>
            </div> : <div className="space-y-0 pb-4" data-id="element-1026" data-mp="xmuweswu61sq">
              {filteredProducts.map((product, index) => {
            const selectedSubstitute = substitutes[product.id];
            const isAutoLinked = autoLinked.has(product.id);
            const hasSubstitute = selectedSubstitute !== null && selectedSubstitute !== undefined;
            return <div key={product.id} className={`py-3 flex items-center justify-between gap-4 ${index !== filteredProducts.length - 1 ? 'border-b border-[#E2E8F0]' : ''}`} data-id="element-1027" data-mp="eus94ypovn9i" data-mpi={index}>
                    {/* Left: Product Info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-1028" data-mp="ntvvqco2eksr">
                      <div className="w-10 h-10 rounded bg-[#F1F5F9] flex items-center justify-center text-2xl flex-shrink-0" data-id="element-1029" data-mp="slnjkffu6cf7">
                        {product.thumbnail}
                      </div>
                      <div className="min-w-0" data-id="element-1030" data-mp="4b83ggyuzd5">
                        <p className="text-[14px] font-medium text-[#0F172A] truncate" data-id="element-1031" data-mp="303xfjlzpli">
                          {product.name}
                        </p>
                        <p className="text-[12px] text-[#64748B]" data-id="element-1032" data-mp="3jsy2hw9xuqod">
                          SKU: {product.sku}
                        </p>
                      </div>
                    </div>

                    {/* Right: Substitute Dropdown */}
                    <div className="flex items-center gap-2" data-id="element-1033" data-mp="3atj9oq6au40p">
                      <select value={selectedSubstitute || ''} onChange={e => handleSubstituteChange(product.id, e.target.value)} className="h-[40px] px-3 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_8px_center] bg-no-repeat w-[240px]" data-id="element-1034" data-mp="35svj25lzfp70">
                        <option value="" data-id="element-1035" data-mp="svb33kcc9q6">Select substitute...</option>
                        {MOCK_PRODUCTS.filter(p => p.id !== product.id).map(p => <option key={p.id} value={p.id} data-id="element-1036" data-mp="3n8psgxuxssmp">
                              {p.name}
                            </option>)}
                      </select>
                      {hasSubstitute && !isAutoLinked && <Check className="w-4 h-4 text-[#10B981] flex-shrink-0" data-id="element-1037" data-mp="3e81e221msq9a" />}
                      {isAutoLinked && <span className="text-[10px] px-2 py-1 bg-[#F1F5F9] text-[#64748B] rounded whitespace-nowrap flex-shrink-0" data-id="element-1038" data-mp="3i9ml36rhiabk">
                          Auto-linked
                        </span>}
                    </div>
                  </div>;
          })}
            </div>}
        </div>

        {/* Fixed Footer - Always visible, does not scroll */}
        <div className="flex-shrink-0 border-t border-[#E2E8F0] bg-white rounded-b-xl" data-id="element-1039" data-mp="2pny189gr64et">
          <div className="p-4 flex items-center justify-between" data-id="element-1040" data-mp="2kp4vj37pjhp7">
            <p className="text-[14px] text-[#64748B]" data-id="element-1041" data-mp="2zl2pceu5s9y9">
              {configuredCount} of {MOCK_PRODUCTS.length} products configured
            </p>
            <div className="flex items-center gap-3" data-id="element-1042" data-mp="2ukofcr8zypxl">
              <button onClick={onClose} className="h-[40px] px-5 text-[14px] font-medium text-[#475569] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-1043" data-mp="38lr090o99u6x">
                Cancel
              </button>
              <button onClick={handleSave} className="h-[40px] px-5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-1044" data-mp="3icdcckwrk2kv">
                Save Changes
              </button>
            </div>
          </div>

          {/* Notification Note */}
          <div className="px-6 pb-4 bg-[#F8FAFC]" data-id="element-1045" data-mp="3nawwqgvpl6b4">
            <p className="text-[12px] text-[#64748B] text-center" data-id="element-1046" data-mp="3nchv6hl599e9">
              Customers will be notified when a substitute is applied to their
              order
            </p>
          </div>
        </div>
      </div>
    </div>;
}